import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { identityFetch } from '../api/identity';
import { getProfile } from '../api/client';
import { useMutation } from '../lib/use-api';
import { ErrorNotice } from './Feedback';
import type { AdminUser } from '../pages/UsersPage';

/**
 * Chỉ system_admin mới được cấp quyền system_admin (server đã kiểm tra).
 */
const BASE_ROLES = ['tenant_admin', 'author', 'teacher', 'learner'] as const;

/** Tạo tài khoản mới, hoặc sửa vai trò của tài khoản đã có. */
export function UserDialog({
  user,
  onClose,
  onSaved,
}: {
  user?: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const editing = user !== undefined;
  // Người dùng LTI/nhúng đăng nhập qua hệ ngoài — đặt mật khẩu cho họ vô nghĩa.
  const hasPassword = !editing || user.source === 'local';
  const profile = getProfile();
  const isSystemAdmin = profile?.roles?.includes('system_admin');

  const assignableRoles = isSystemAdmin ? (['system_admin', ...BASE_ROLES] as const) : BASE_ROLES;

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState<string[]>(user?.roles ?? ['learner']);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const save = useMutation<void, void>(
    useCallback(async () => {
      if (editing) {
        await identityFetch<void>(`/api/admin/users/${user.principalId}/roles`, {
          method: 'PUT',
          body: { roles },
        });
        if (password) {
          await identityFetch<void>(`/api/admin/users/${user.principalId}/password`, {
            method: 'POST',
            body: { password },
          });
        }
        return;
      }
      await identityFetch<{ principalId: string }>('/api/admin/users', {
        method: 'POST',
        body: { email: email.trim(), displayName: displayName.trim(), password, roles },
      });
    }, [editing, user?.principalId, roles, password, email, displayName]),
  );

  const submit = async (): Promise<void> => {
    const done = await save.run();
    if (done !== undefined) onSaved();
  };

  const toggleRole = (role: string): void => {
    setRoles((current) =>
      current.includes(role) ? current.filter((r) => r !== role) : [...current, role],
    );
  };

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>
        {editing ? t('users.editHeading', { name: user.displayName }) : t('users.createHeading')}
      </h2>

      {!editing ? (
        <>
          <label className="field">
            <span>{t('users.column.displayName')}</span>
            <input
              type="text"
              value={displayName}
              required
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>

          <label className="field">
            <span>{t('users.column.email')}</span>
            <input
              type="email"
              value={email}
              required
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
        </>
      ) : null}

      {hasPassword ? (
        <label className="field">
          <span>{editing ? t('users.newPassword') : t('users.password')}</span>
          <input
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(event) => setPassword(event.target.value)}
          />
          <small className="hint">
            {editing ? t('users.newPasswordHint') : t('users.passwordHint')}
          </small>
        </label>
      ) : null}

      <fieldset className="field">
        <legend>{t('users.column.roles')}</legend>
        {assignableRoles.map((role) => (
          <label key={role} className="field--inline">
            <input
              type="checkbox"
              checked={roles.includes(role)}
              onChange={() => toggleRole(role)}
            />
            <span>{t(`users.role.${role}`)}</span>
          </label>
        ))}
      </fieldset>

      {save.error ? <ErrorNotice error={save.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button
          type="button"
          disabled={save.pending || roles.length === 0}
          onClick={() => void submit()}
        >
          {t('common:action.save')}
        </button>
      </footer>
    </dialog>
  );
}
